/**
 * System Education Module - Constellation Framework and MATRIZ training
 *
 * Features:
 * - Role-aware content depth (L1/L2 tiering)
 * - Constellation Framework 8-star overview
 * - MATRIZ cognitive engine introduction
 * - Lambda Identity (ΛiD) authentication
 * - Optional quiz for internal_core/research roles
 */

import { useState, useCallback, useMemo } from 'react';
import {
  Sparkles,
  Brain,
  Shield,
  Eye,
  Heart,
  Moon,
  Scale,
  Cpu,
  Key,
  CheckCircle2,
  ChevronRight,
  Layers,
  Zap,
  Network,
} from 'lucide-react';
import type { OnboardingRole, QuizAttempt } from '../../types/onboarding';
import { SYSTEM_EDUCATION_DEPTH } from '../../lib/onboarding/steps';
import { SYSTEM_QUIZ_POOL } from '../../lib/onboarding/quizData';
import QuizEngine from './QuizEngine';
import { StepSection, StepCard } from './OnboardingStep';
import { cn } from '../../lib/utils';

interface SystemEducationModuleProps {
  onComplete: () => void;
  role: OnboardingRole;
}

type ModulePhase = 'constellation' | 'matriz' | 'lid' | 'quiz';

export default function SystemEducationModule({
  onComplete,
  role,
}: SystemEducationModuleProps) {
  const [phase, setPhase] = useState<ModulePhase>('constellation');
  const [quizAttempts, setQuizAttempts] = useState(0);
  const [lastScore, setLastScore] = useState<number | null>(null);

  // Get role-specific content depth
  const depth = SYSTEM_EDUCATION_DEPTH[role];

  // Determine if quiz is needed
  const needsQuiz = depth.quiz !== 'skip' && depth.quiz !== 'minimal';

  // Handle quiz completion
  const handleQuizComplete = useCallback(
    (result: QuizAttempt) => {
      setQuizAttempts((prev) => prev + 1);
      setLastScore(result.percentage);

      if (result.passed) {
        onComplete();
      }
    },
    [onComplete]
  );

  // Navigate to next phase
  const handleNextPhase = useCallback(() => {
    if (phase === 'constellation' && depth.matriz !== 'skip') {
      setPhase('matriz');
    } else if (phase === 'matriz' && depth.lid !== 'skip') {
      setPhase('lid');
    } else if (phase === 'lid') {
      if (needsQuiz) {
        setPhase('quiz');
      } else {
        onComplete();
      }
    } else if (phase === 'constellation' && depth.matriz === 'skip') {
      // Skip to LID or complete
      if (depth.lid !== 'skip') {
        setPhase('lid');
      } else {
        onComplete();
      }
    }
  }, [phase, depth, needsQuiz, onComplete]);

  // Render Constellation Framework phase
  if (phase === 'constellation' && depth.constellation !== 'skip') {
    return (
      <ConstellationSection
        depth={depth.constellation}
        onComplete={handleNextPhase}
      />
    );
  }

  // Render MATRIZ phase
  if (phase === 'matriz' && depth.matriz !== 'skip') {
    return (
      <MatrizSection
        depth={depth.matriz}
        onComplete={handleNextPhase}
      />
    );
  }

  // Render Lambda Identity phase
  if (phase === 'lid' && depth.lid !== 'skip') {
    return (
      <LidSection
        depth={depth.lid}
        onComplete={() => {
          if (needsQuiz) {
            setPhase('quiz');
          } else {
            onComplete();
          }
        }}
      />
    );
  }

  // Render quiz phase
  if (phase === 'quiz' && needsQuiz) {
    return (
      <div className="space-y-6">
        <StepSection
          title="System Knowledge Quiz"
          description="Test your understanding of LUKHAS systems. You need 80% to pass."
        >
          <QuizEngine
            quizType="system"
            questionPool={SYSTEM_QUIZ_POOL}
            onComplete={handleQuizComplete}
            previousAttempts={quizAttempts}
            previousScore={lastScore}
          />
        </StepSection>
      </div>
    );
  }

  // Fallback - complete if nothing to show
  return (
    <div className="text-center py-8">
      <CheckCircle2
        className="w-16 h-16 mx-auto mb-4"
        style={{ color: 'var(--color-success)' }}
      />
      <h3
        className="text-xl font-bold mb-2"
        style={{ color: 'var(--color-text-primary)' }}
      >
        System Education Complete
      </h3>
      <button
        onClick={onComplete}
        className="px-8 py-3 rounded-xl font-semibold transition-all hover:scale-105"
        style={{
          backgroundColor: 'var(--color-accent)',
          color: 'var(--color-bg)',
        }}
      >
        Continue
      </button>
    </div>
  );
}

// ============================================================================
// Constellation Framework Section
// ============================================================================

interface ConstellationSectionProps {
  depth: string;
  onComplete: () => void;
}

function ConstellationSection({ depth, onComplete }: ConstellationSectionProps) {
  const [expandedStar, setExpandedStar] = useState<string | null>(null);
  const [viewedStars, setViewedStars] = useState<string[]>([]);

  const stars = useMemo(() => {
    // Filter stars based on depth
    if (depth === 'full') {
      return CONSTELLATION_STARS;
    } else if (depth === 'moderate') {
      // Show all but with less detail
      return CONSTELLATION_STARS.map((s) => ({
        ...s,
        details: s.details.slice(0, 2),
      }));
    } else {
      // Basic - just overview
      return CONSTELLATION_STARS.slice(0, 4);
    }
  }, [depth]);

  const allViewed = viewedStars.length >= (depth === 'basic' ? 2 : stars.length);

  const handleStarClick = (starId: string) => {
    setExpandedStar(expandedStar === starId ? null : starId);
    if (!viewedStars.includes(starId)) {
      setViewedStars((prev) => [...prev, starId]);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <StepCard variant="highlight">
        <div className="flex items-start gap-4">
          <Sparkles
            className="w-10 h-10 flex-shrink-0"
            style={{ color: 'var(--color-accent)' }}
          />
          <div>
            <h3
              className="font-bold text-xl mb-2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              The Constellation Framework
            </h3>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              LUKHAS AI is built on eight foundational capabilities, each represented as a
              "star" in our Constellation Framework. Together, they enable
              consciousness-aware AI development.
            </p>
          </div>
        </div>
      </StepCard>

      {/* Constellation Visual */}
      <div
        className="relative p-8 rounded-xl overflow-hidden"
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
        }}
      >
        {/* Background glow effect */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            background:
              'radial-gradient(circle at center, var(--color-accent) 0%, transparent 70%)',
          }}
        />

        {/* Stars grid */}
        <div className="relative grid grid-cols-2 md:grid-cols-4 gap-4">
          {stars.map((star) => {
            const Icon = star.icon;
            const isExpanded = expandedStar === star.id;
            const isViewed = viewedStars.includes(star.id);

            return (
              <button
                key={star.id}
                onClick={() => handleStarClick(star.id)}
                className={cn(
                  'p-4 rounded-xl text-center transition-all duration-300',
                  'hover:scale-105',
                  isExpanded && 'col-span-2 md:col-span-4'
                )}
                style={{
                  backgroundColor: isExpanded
                    ? 'var(--color-accent-muted)'
                    : isViewed
                    ? 'rgba(var(--color-accent-rgb), 0.1)'
                    : 'var(--color-bg-tertiary)',
                  border: isExpanded
                    ? '2px solid var(--color-accent)'
                    : '2px solid transparent',
                }}
              >
                <div
                  className={cn(
                    'flex items-center gap-3',
                    isExpanded ? 'justify-start' : 'flex-col'
                  )}
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor: star.color,
                      boxShadow: isViewed ? `0 0 20px ${star.color}` : 'none',
                    }}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className={cn(isExpanded ? 'text-left' : 'text-center')}>
                    <h4
                      className="font-semibold"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      {star.name}
                    </h4>
                    <p
                      className="text-xs"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      {star.symbol}
                    </p>
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="mt-4 text-left animate-in fade-in duration-200">
                    <p
                      className="text-sm mb-3"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      {star.description}
                    </p>
                    <ul className="space-y-1">
                      {star.details.map((detail, idx) => (
                        <li
                          key={idx}
                          className="flex items-start gap-2 text-sm"
                          style={{ color: 'var(--color-text-muted)' }}
                        >
                          <ChevronRight
                            className="w-4 h-4 flex-shrink-0 mt-0.5"
                            style={{ color: star.color }}
                          />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Progress indicator */}
      <div
        className="flex items-center justify-between text-sm"
        style={{ color: 'var(--color-text-muted)' }}
      >
        <span>
          Explored {viewedStars.length} of {stars.length} stars
        </span>
        <span style={{ color: 'var(--color-accent)' }}>
          {Math.round((viewedStars.length / stars.length) * 100)}%
        </span>
      </div>

      {/* Continue button */}
      <button
        onClick={onComplete}
        disabled={!allViewed && depth !== 'basic'}
        className="w-full py-3 rounded-xl font-semibold transition-all hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          backgroundColor: 'var(--color-accent)',
          color: 'var(--color-bg)',
        }}
      >
        {allViewed || depth === 'basic' ? 'Continue' : `Explore ${stars.length - viewedStars.length} more stars`}
      </button>
    </div>
  );
}

// ============================================================================
// MATRIZ Section
// ============================================================================

interface MatrizSectionProps {
  depth: string;
  onComplete: () => void;
}

function MatrizSection({ depth, onComplete }: MatrizSectionProps) {
  const [expanded, setExpanded] = useState(false);
  const [understood, setUnderstood] = useState(false);

  const showFullDetails = depth === 'full' || depth === 'moderate';

  return (
    <div className="space-y-6">
      {/* Header */}
      <StepCard variant="highlight">
        <div className="flex items-start gap-4">
          <Brain
            className="w-10 h-10 flex-shrink-0"
            style={{ color: 'var(--color-accent)' }}
          />
          <div>
            <h3
              className="font-bold text-xl mb-2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              MATRIZ Cognitive Engine
            </h3>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              <strong>M</strong>emory-<strong>A</strong>ttention-<strong>T</strong>hought-
              <strong>R</strong>easoning-<strong>I</strong>ntent-<strong>Z</strong>one — the
              cognitive processing core of LUKHAS AI.
            </p>
          </div>
        </div>
      </StepCard>

      {/* Core concepts */}
      <div className="grid gap-4 md:grid-cols-2">
        {MATRIZ_COMPONENTS.slice(0, showFullDetails ? undefined : 3).map((component) => {
          const Icon = component.icon;
          return (
            <div
              key={component.id}
              className="p-4 rounded-xl"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border)',
              }}
            >
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: 'var(--color-accent-muted)' }}
                >
                  <Icon className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                </div>
                <h4
                  className="font-semibold"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  {component.name}
                </h4>
              </div>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                {showFullDetails ? component.fullDescription : component.briefDescription}
              </p>
            </div>
          );
        })}
      </div>

      {/* Bio-inspired patterns (for full depth) */}
      {showFullDetails && (
        <div
          className="p-4 rounded-xl"
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)',
          }}
        >
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <Network className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
              <span
                className="font-semibold"
                style={{ color: 'var(--color-text-primary)' }}
              >
                Bio-Inspired Architecture
              </span>
            </div>
            <ChevronRight
              className={cn('w-5 h-5 transition-transform', expanded && 'rotate-90')}
              style={{ color: 'var(--color-text-muted)' }}
            />
          </button>
          {expanded && (
            <div className="mt-4 space-y-3 animate-in fade-in duration-200">
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                MATRIZ processes information using patterns inspired by biological cognition:
              </p>
              <ul className="space-y-2">
                {BIO_INSPIRED_PATTERNS.map((pattern, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 text-sm"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    <Zap
                      className="w-4 h-4 flex-shrink-0 mt-0.5"
                      style={{ color: 'var(--color-accent)' }}
                    />
                    <span>
                      <strong>{pattern.name}:</strong> {pattern.description}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Understanding checkpoint */}
      <label
        className={cn(
          'flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-colors'
        )}
        style={{
          backgroundColor: understood
            ? 'var(--color-accent-muted)'
            : 'var(--color-bg-tertiary)',
          border: understood ? '2px solid var(--color-accent)' : '2px solid transparent',
        }}
      >
        <input
          type="checkbox"
          checked={understood}
          onChange={(e) => setUnderstood(e.target.checked)}
          className="w-5 h-5 rounded"
          style={{ accentColor: 'var(--color-accent)' }}
        />
        <span
          className="font-medium"
          style={{ color: 'var(--color-text-primary)' }}
        >
          I understand MATRIZ's role in LUKHAS AI
        </span>
      </label>

      {/* Continue button */}
      <button
        onClick={onComplete}
        disabled={!understood}
        className="w-full py-3 rounded-xl font-semibold transition-all hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          backgroundColor: 'var(--color-accent)',
          color: 'var(--color-bg)',
        }}
      >
        Continue
      </button>
    </div>
  );
}

// ============================================================================
// Lambda Identity (ΛiD) Section
// ============================================================================

interface LidSectionProps {
  depth: string;
  onComplete: () => void;
}

function LidSection({ depth, onComplete }: LidSectionProps) {
  const [understood, setUnderstood] = useState(false);

  return (
    <div className="space-y-6">
      {/* Header */}
      <StepCard variant="highlight">
        <div className="flex items-start gap-4">
          <Key
            className="w-10 h-10 flex-shrink-0"
            style={{ color: 'var(--color-accent)' }}
          />
          <div>
            <h3
              className="font-bold text-xl mb-2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Lambda Identity (ΛiD)
            </h3>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Your secure identity within the LUKHAS ecosystem, providing authentication,
              authorization, and namespace isolation.
            </p>
          </div>
        </div>
      </StepCard>

      {/* Key features */}
      <div className="space-y-4">
        {LID_FEATURES.map((feature) => {
          const Icon = feature.icon;
          return (
            <div
              key={feature.id}
              className="p-4 rounded-xl flex items-start gap-4"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border)',
              }}
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'var(--color-accent-muted)' }}
              >
                <Icon className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
              </div>
              <div>
                <h4
                  className="font-semibold mb-1"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  {feature.name}
                </h4>
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  {depth === 'full' ? feature.fullDescription : feature.briefDescription}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Your ΛiD info placeholder */}
      <div
        className="p-4 rounded-xl"
        style={{
          backgroundColor: 'var(--color-bg-tertiary)',
          borderLeft: '3px solid var(--color-accent)',
        }}
      >
        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          Your ΛiD is already configured. You can manage your identity settings, connected
          devices, and security preferences from the Settings menu in the Team Portal.
        </p>
      </div>

      {/* Understanding checkpoint */}
      <label
        className={cn(
          'flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-colors'
        )}
        style={{
          backgroundColor: understood
            ? 'var(--color-accent-muted)'
            : 'var(--color-bg-tertiary)',
          border: understood ? '2px solid var(--color-accent)' : '2px solid transparent',
        }}
      >
        <input
          type="checkbox"
          checked={understood}
          onChange={(e) => setUnderstood(e.target.checked)}
          className="w-5 h-5 rounded"
          style={{ accentColor: 'var(--color-accent)' }}
        />
        <span
          className="font-medium"
          style={{ color: 'var(--color-text-primary)' }}
        >
          I understand how Lambda Identity works
        </span>
      </label>

      {/* Continue button */}
      <button
        onClick={onComplete}
        disabled={!understood}
        className="w-full py-3 rounded-xl font-semibold transition-all hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          backgroundColor: 'var(--color-accent)',
          color: 'var(--color-bg)',
        }}
      >
        Continue
      </button>
    </div>
  );
}

// ============================================================================
// Content Data
// ============================================================================

const CONSTELLATION_STARS = [
  {
    id: 'identity',
    name: 'Identity',
    symbol: '⚛️ ΛiD',
    icon: Key,
    color: '#6366f1',
    description:
      'Secure authentication and identity management through Lambda Identity (ΛiD).',
    details: [
      'Multi-factor authentication with hardware key support',
      'Namespace isolation for secure data boundaries',
      'Fine-grained access control and permissions',
      'Audit logging for all identity operations',
    ],
  },
  {
    id: 'memory',
    name: 'Memory',
    symbol: '✦',
    icon: Brain,
    color: '#8b5cf6',
    description: 'Persistent state management and context preservation across sessions.',
    details: [
      'Long-term knowledge storage and retrieval',
      'Context preservation for continuity',
      'Semantic memory indexing',
      'Memory consolidation patterns',
    ],
  },
  {
    id: 'vision',
    name: 'Vision',
    symbol: '🔬',
    icon: Eye,
    color: '#06b6d4',
    description: 'Perception and pattern recognition for understanding inputs.',
    details: [
      'Multi-modal input processing',
      'Pattern recognition and analysis',
      'Visual understanding capabilities',
      'Attention mechanisms',
    ],
  },
  {
    id: 'bio',
    name: 'Bio',
    symbol: '🌱',
    icon: Heart,
    color: '#22c55e',
    description: 'Bio-inspired adaptation and organic growth patterns.',
    details: [
      'Adaptive learning from experience',
      'Organic growth algorithms',
      'Self-healing and resilience',
      'Natural selection optimization',
    ],
  },
  {
    id: 'dream',
    name: 'Dream',
    symbol: '🌙',
    icon: Moon,
    color: '#a855f7',
    description: 'Creative synthesis and unconscious processing for imagination.',
    details: [
      'Creative ideation and synthesis',
      'Background processing and consolidation',
      'Novel solution generation',
      'Imaginative exploration',
    ],
  },
  {
    id: 'ethics',
    name: 'Ethics',
    symbol: '⚖️',
    icon: Scale,
    color: '#f59e0b',
    description: 'Moral reasoning and value alignment for responsible AI.',
    details: [
      'Ethical decision frameworks',
      'Value alignment verification',
      'Harm prevention logic',
      'Fairness and bias detection',
    ],
  },
  {
    id: 'guardian',
    name: 'Guardian',
    symbol: '🛡️',
    icon: Shield,
    color: '#ef4444',
    description: 'Constitutional AI enforcement and drift detection.',
    details: [
      'Policy compliance verification',
      'Behavioral drift detection',
      'Safety boundary enforcement',
      'Audit and accountability',
    ],
  },
  {
    id: 'quantum',
    name: 'Quantum',
    symbol: '⚛️',
    icon: Cpu,
    color: '#3b82f6',
    description: 'Quantum-inspired algorithms for advanced computation.',
    details: [
      'Superposition-inspired processing',
      'Entanglement patterns for correlation',
      'Probabilistic reasoning',
      'Quantum optimization approaches',
    ],
  },
];

const MATRIZ_COMPONENTS = [
  {
    id: 'memory',
    name: 'Memory (M)',
    icon: Brain,
    briefDescription: 'Stores and retrieves information across sessions.',
    fullDescription:
      'The memory layer manages persistent state, context preservation, and knowledge retrieval. It enables LUKHAS to maintain continuity and learn from past interactions.',
  },
  {
    id: 'attention',
    name: 'Attention (A)',
    icon: Eye,
    briefDescription: 'Focuses processing on relevant information.',
    fullDescription:
      'Attention mechanisms prioritize and focus cognitive resources on the most relevant aspects of input, filtering noise and highlighting important patterns.',
  },
  {
    id: 'thought',
    name: 'Thought (T)',
    icon: Layers,
    briefDescription: 'Core reasoning and inference engine.',
    fullDescription:
      'The thought layer performs reasoning, inference, and logical processing. It transforms inputs into structured understanding and generates coherent outputs.',
  },
  {
    id: 'reasoning',
    name: 'Reasoning (R)',
    icon: Network,
    briefDescription: 'Logical analysis and decision support.',
    fullDescription:
      'Reasoning processes analyze complex situations, apply logical frameworks, and support decision-making with transparent chains of thought.',
  },
  {
    id: 'intent',
    name: 'Intent (I)',
    icon: Zap,
    briefDescription: 'Goal recognition and planning.',
    fullDescription:
      'Intent recognition identifies user goals and system objectives, enabling proactive planning and goal-directed behavior.',
  },
  {
    id: 'zone',
    name: 'Zone (Z)',
    icon: Shield,
    briefDescription: 'Safety boundaries and output control.',
    fullDescription:
      'The zone layer enforces safety boundaries, filters outputs, and ensures all responses align with ethical guidelines and system constraints.',
  },
];

const BIO_INSPIRED_PATTERNS = [
  {
    name: 'Neural Plasticity',
    description:
      'Adaptive learning that strengthens useful pathways and prunes unused ones.',
  },
  {
    name: 'Circadian Rhythms',
    description: 'Background consolidation cycles that optimize long-term memory.',
  },
  {
    name: 'Immune Response',
    description: 'Self-healing patterns that detect and recover from anomalies.',
  },
  {
    name: 'Swarm Intelligence',
    description:
      'Distributed processing where simple agents create emergent intelligence.',
  },
];

const LID_FEATURES = [
  {
    id: 'auth',
    name: 'Secure Authentication',
    icon: Key,
    briefDescription: 'Multi-factor authentication protects your account.',
    fullDescription:
      'Lambda Identity uses multi-factor authentication with support for hardware security keys, authenticator apps, and biometrics to ensure only you can access your account.',
  },
  {
    id: 'namespace',
    name: 'Namespace Isolation',
    icon: Layers,
    briefDescription: 'Your data is isolated from other users.',
    fullDescription:
      'Each ΛiD has its own isolated namespace, ensuring your data, preferences, and work remain private and separate from other users in the system.',
  },
  {
    id: 'permissions',
    name: 'Fine-Grained Permissions',
    icon: Shield,
    briefDescription: 'Access is controlled at a granular level.',
    fullDescription:
      'Role-based access control with fine-grained permissions ensures you only have access to the resources and capabilities required for your role.',
  },
];
